import { TAG_MODEL } from '@/lib/constants/model-types';
import capitalizeString from '@/lib/helpers/capitalize-string';

const UI_REGEX = /ui-/i;

const createModel = (Klass, component, uiProps, customData = {}) => {
  const model = new Klass(component, {
    name: component.name,
    uiProps,
    ...customData,
  });

  return model;
};

const extractAttrsForUiProps = (component) => {
  const attrs = component.$attrs;

  const uiProps = Object.keys(attrs)
    .filter(val => UI_REGEX.test(val))
    .reduce((obj, key) => {
      const newKey = key.replace(UI_REGEX, '');
      obj[newKey] = attrs[key];
      return obj;
    }, {});

  return uiProps;
};

const defineComponentModel = (klassModel, objComponent) => {
  const klassName = klassModel.$modelName;
  const capitalizeKlassName = capitalizeString(klassName);

  const parentName = klassModel.$parentName;
  const capitalizeParentName = capitalizeString(parentName);

  let renderFn = objComponent.render;
  const createdFn = objComponent.created;

  if (!renderFn) {
    renderFn = (h, defaultSlot) => h(
      TAG_MODEL,
      [
        ...defaultSlot,
      ],
    );
  }

  const generated = {
    name: `${capitalizeKlassName}Model`,
    inject: [`get${capitalizeParentName}`],

    methods: {
      customDataForModel() {
        if (objComponent.customDataForModel) {
          return objComponent.customDataForModel.apply(this);
        }

        return {};
      },
    },

    provide() {
      return {
        [`get${capitalizeKlassName}`]: () => this.model,
      };
    },

    props: objComponent.props,

    created() {
      const uiProps = extractAttrsForUiProps(this);
      const customDataForModel = this.customDataForModel();

      this.model = createModel(klassModel, this, uiProps, customDataForModel);

      if (createdFn) {
        createdFn.bind(this)();
      }
    },

    beforeUpdate() {
      const uiProps = extractAttrsForUiProps(this);
      this.model.uiProps = uiProps;
      if (this.model.presence !== this.presence) {
        this.model.presence = this.presence;
      }
      if (this.model.blocker !== this.blocker) {
        this.model.blocker = this.blocker;
      }
    },

    mounted() {
      const parentModel = this[`get${capitalizeParentName}`]();
      parentModel[`add${capitalizeKlassName}`](this.model);
    },

    destroyed() {
      const parentModel = this[`get${capitalizeParentName}`]();
      parentModel[`remove${capitalizeKlassName}`](this.model);
    },

    render(h) {
      const defaultSlot = this.$slots.default;
      // TODO: This should not be on use
      // Used because use of scopedSlots
      if (this.model.$rendererComponent) {
        this.model.$rendererComponent.$forceUpdate();
      }

      return renderFn(h, defaultSlot);
    },
  };

  return generated;
};

export default defineComponentModel;
